import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import type { ReactNode } from "react";
import i18n from "./i18n";
import { useAuthStore } from "./store/auth";
import { SocketProvider } from "./socket/provider";
import { usePermissions } from "./hooks/usePermissions";
import { ToastProvider } from "./components/Toast";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ConfirmDialogProvider } from "./components/ConfirmDialog";

import { LoginScreen } from "./screens/Login";
import { DashboardScreen } from "./screens/Dashboard";
import { OrdersScreen } from "./screens/Orders";
import { OrderDetailScreen } from "./screens/OrderDetail";
import { TransactionsScreen } from "./screens/Transactions";
import { TransactionDetailScreen } from "./screens/TransactionDetail";
import { IssueRefundScreen } from "./screens/IssueRefund";
import { RefundsScreen } from "./screens/Refunds";
import { RefundDetailScreen } from "./screens/RefundDetail";
import { MenuScreen } from "./screens/Menu";
import { AddMenuItemScreen } from "./screens/AddMenuItemScreen";
import { VouchersScreen } from "./screens/Vouchers";
import { StaffScreen } from "./screens/Staff";
import { NotFoundScreen } from "./screens/NotFound";
import { CustomersScreen } from "./screens/Customers";
import { AdminAccountsScreen } from "./screens/AdminAccounts";
import { ReportsScreen } from "./screens/Reports";
import { ActivityLogScreen } from "./screens/ActivityLog";
import { FeatureFlagsScreen } from "./screens/FeatureFlags";
import { PricingRulesScreen } from "./screens/PricingRules";
import { HoursScreen } from "./screens/Hours";
import { SettingsScreen } from "./screens/Settings";
import { BundlesScreen } from "./screens/Bundles";
import { StallsScreen } from "./screens/Stalls";
import { StallApplicationsScreen } from "./screens/StallApplications";
import { PaymentMethodsScreen } from "./screens/PaymentMethods";
import { DeliveryProvidersScreen } from "./screens/DeliveryProviders";
import { SmsProvidersScreen } from "./screens/SmsProviders";
import { BannersScreen } from "./screens/Banners";
import { CategoriesScreen } from "./screens/Categories";
import { LegalContentScreen } from "./screens/LegalContent";

function Protected({ children, routePath }: { children: ReactNode; routePath?: string }) {
  const token = useAuthStore((s) => s.accessToken);
  const { canRoute } = usePermissions();
  if (!token) return <Navigate to="/login" replace />;
  // If a routePath is provided, check RBAC — redirect to "/" if not allowed
  if (routePath && !canRoute(routePath)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Guarded({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.querySelector("#admin-content")?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search]);

  return null;
}

export function App() {
  return (
    <ToastProvider>
      <ConfirmDialogProvider>
      <BrowserRouter basename={import.meta.env.BASE_URL} future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <ScrollToTop />
        <SocketProvider>
          <Guarded>
          <Routes>
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/" element={<Protected routePath="/"><DashboardScreen /></Protected>} />
            <Route path="/orders" element={<Protected routePath="/orders"><OrdersScreen /></Protected>} />
            <Route path="/orders/:id" element={<Protected routePath="/orders"><OrderDetailScreen /></Protected>} />
            <Route path="/transactions" element={<Protected routePath="/transactions"><TransactionsScreen /></Protected>} />
            <Route path="/transactions/:id" element={<Protected routePath="/transactions"><TransactionDetailScreen /></Protected>} />
            <Route path="/transactions/:id/refund" element={<Protected routePath="/transactions"><IssueRefundScreen /></Protected>} />
            <Route path="/refunds" element={<Protected routePath="/refunds"><RefundsScreen /></Protected>} />
            <Route path="/refunds/:id" element={<Protected routePath="/refunds"><RefundDetailScreen /></Protected>} />
            <Route path="/menu" element={<Protected routePath="/menu"><MenuScreen /></Protected>} />
            <Route path="/menu/items/new" element={<Protected routePath="/menu"><AddMenuItemScreen /></Protected>} />
            <Route path="/menu/items/edit" element={<Protected routePath="/menu"><AddMenuItemScreen /></Protected>} />
            <Route path="/categories" element={<Protected routePath="/categories"><CategoriesScreen /></Protected>} />
            <Route path="/vouchers" element={<Protected routePath="/vouchers"><VouchersScreen /></Protected>} />
            <Route path="/staff" element={<Protected routePath="/staff"><StaffScreen /></Protected>} />
            <Route path="/customers" element={<Protected routePath="/customers"><CustomersScreen /></Protected>} />
            <Route path="/admins" element={<Protected routePath="/admins"><AdminAccountsScreen /></Protected>} />
            <Route path="/reports" element={<Protected routePath="/reports"><ReportsScreen /></Protected>} />
            <Route path="/activity" element={<Protected routePath="/activity"><ActivityLogScreen /></Protected>} />
            <Route path="/flags" element={<Protected routePath="/flags"><FeatureFlagsScreen /></Protected>} />
            <Route path="/pricing-rules" element={<Protected routePath="/pricing-rules"><PricingRulesScreen /></Protected>} />
            <Route path="/hours" element={<Protected routePath="/hours"><HoursScreen /></Protected>} />
            <Route path="/legal-content" element={<Protected routePath="/legal-content"><LegalContentScreen /></Protected>} />
            <Route path="/settings" element={<Protected routePath="/settings"><SettingsScreen /></Protected>} />
            <Route path="/bundles" element={<Protected routePath="/bundles"><BundlesScreen /></Protected>} />
            <Route path="/stalls" element={<Protected routePath="/stalls"><StallsScreen /></Protected>} />
            <Route path="/stall-applications" element={<Protected routePath="/stall-applications"><StallApplicationsScreen /></Protected>} />
            <Route path="/payment-methods" element={<Protected routePath="/payment-methods"><PaymentMethodsScreen /></Protected>} />
            <Route path="/delivery-providers" element={<Protected routePath="/delivery-providers"><DeliveryProvidersScreen /></Protected>} />
            <Route path="/sms-providers" element={<Protected routePath="/sms-providers"><SmsProvidersScreen /></Protected>} />
            <Route path="/banners" element={<Protected routePath="/banners"><BannersScreen /></Protected>} />
            <Route path="*" element={<Protected routePath="/"><NotFoundScreen /></Protected>} />
          </Routes>
          </Guarded>
        </SocketProvider>
      </BrowserRouter>
      </ConfirmDialogProvider>
    </ToastProvider>
  );
}
